import "./style/bundle-section-header.scss"
import type { RootState } from "@app/types"
import React, { useEffect, useRef } from "react"
import { CampaignPageNavigator } from "./types/CampaignAdminTypes"
import AppLink from "@modules/shared/AppLink"
import { FiChevronRight } from "@ng-mw/reol/icon-components/FiChevronRight"
import { trackViewPromotion, useAutomaticTrackingData } from "@ng-mw/shared-react-components"
import { useSelector } from "react-redux"

interface BundleSectionHeaderProps {
    title: string
    campaignPageNavigator: CampaignPageNavigator
}

const BundleSectionHeader = ({ title, campaignPageNavigator }: BundleSectionHeaderProps) => {
    const automaticTrackingData = useAutomaticTrackingData()
    const storeGln = useSelector(
        (state: RootState): string | undefined => state.user.data?.store?.gln,
    )
    const to = `/savings/campaigns/campaign-${campaignPageNavigator.id.join("-")}`
    const promotionId = campaignPageNavigator.id.join("-")
    const state = {
        ...campaignPageNavigator,
    }

    const trackedPromotionId = useRef<string | undefined>(undefined)
    const headerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (trackedPromotionId.current === promotionId) return

        const trackPromotion = () => {
            if (trackedPromotionId.current === promotionId) return

            trackViewPromotion(automaticTrackingData, {
                items: [
                    {
                        promotion_id: promotionId,
                        promotion_name: campaignPageNavigator.campaignName,
                        creative_name: title,
                        creative_slot: "bundle_section_header",
                        location_id: storeGln,
                    },
                ],
            })

            trackedPromotionId.current = promotionId
        }

        const headerElement = headerRef.current

        if (headerElement === null) return

        if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
            trackPromotion()
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry?.isIntersecting) return

            trackPromotion()
            observer.disconnect()
        })

        observer.observe(headerElement)

        return () => observer.disconnect()
    }, [automaticTrackingData, campaignPageNavigator.campaignName, promotionId, storeGln, title])

    return (
        <div
            ref={headerRef}
            className="bundle-section-header"
        >
            <h3 className="bundle-section-header__heading">{title}</h3>
            <AppLink
                to={to}
                state={state}
                className="bundle-section-header__button"
            >
                <FiChevronRight
                    width={24}
                    height={24}
                />
            </AppLink>
        </div>
    )
}

export default BundleSectionHeader
